import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface User {
    id: string
    username?: string
    usernameType?: string
    phone: string
    whatsapp: string
    email: string
    firstName: string
    lastName: string
    profileUrl: string
    permissions?: string[]
}

export interface Member {
    id: string
    userId: string
    membershipTypeId: string
    designation: string
    firstName: string
    secondName: string
    lastName: string
    phone: string
    email: string
    profileUrl: string
    role: string
    isManager: boolean
    isMember: boolean
    isRegistrationComplete: boolean
    hasMembershipRemainder: boolean
    hasAutoBilling: boolean
    membershipNumber: string
    membershipCustomNumber: string
    membershipApproved: boolean
    membershipStartDate: string
    membershipNextBillingDate: string
    isApproved: boolean
    hasExpire: boolean
    isExpired: boolean
    startDate: string
    endDate: string
    status: string
    createdAt: string
    updatedAt: string
    customFormValues: any[]
}

export interface LoginData {
    username: string,
    usernameType: string, // default to email
    password?: string,
    rememberMe?: boolean
}

export interface RegisterForm {
    username?: string;
    firstName: string;
    lastName: string;
    phone: string;
    subdomain: string;
    organization: string;
    email: string;
}

export const useAuthStore = defineStore('auth', () => {
    const isLoading = ref(false)
    const error = ref<string | null>(null)
    const user = ref<User | null>(null)
    const member = ref<Member | null>(null)
    const refreshToken = ref<string | boolean | null>(localStorage.getItem('refreshToken'))
    const accessToken = ref<string | boolean | null>(localStorage.getItem('accessToken'))
    const permissions = ref<string[]>([])
    const isLogin = computed(() => !!accessToken.value && !!refreshToken.value)
    const isAuthenticated = computed(() => !!accessToken.value && !!refreshToken.value)
    const fullName = computed(() => {
        if (user.value) {
            var names = [];
            if (user.value.firstName) names.push(user.value.firstName);
            if (user.value.lastName) names.push(user.value.lastName);
            if (names.length) {
                return names.join(' ');
            } else if (user.value.username) {
                return user.value.username;
            }
        }

        return '';
    })

    const profileUrl = computed(() => {
        if (user.value && user.value.profileUrl) {
            return user.value.profileUrl;
        }
        return `${window.location.origin}/core/images/user-avatar.png`;
    })

    const sendOTP = async (form: LoginData, url?: string): Promise<boolean> => {
        isLoading.value = true
        error.value = null

        try {
            let host = window.location.origin;
            let moduleName = import.meta.env.VITE_MODULE_NAME;
            let prefix = (moduleName == 'register') ? "/register" : "/auth";
            if (!url) url = `${host}${prefix}/graphql/auth`;

            const query = `mutation Login($input:OtpInput!) {
                otp(input:$input) {status,message}
            }`;
            const variables = { input: { ...form } }
            const data = await window.$ajaxGraphql(query, variables, url)

            if (data.errors) {
                var msg = []
                if(Array.isArray(data.errors)) {
                    for (const e of data.errors) {
                        if(typeof e == "string") {
                            msg.push(e)
                        } else {
                            msg.push(e.message || 'Login failed')
                        }
                    }
                }
                error.value = msg.join(", ")
            } else if (data.otp && data.otp.status) {
                return true;
            } else if (data.otp && data.otp.message) {
                error.value = data.otp.message || 'Login failed'
            }
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Login failed'
        } finally {
            isLoading.value = false
        }

        return false
    }

    const login = async (form: LoginData, loadUser?: boolean, url?: string) => {
        isLoading.value = true
        error.value = null

        try {
            let host = window.location.origin;
            let moduleName = import.meta.env.VITE_MODULE_NAME;
            let prefix = (moduleName == 'register') ? "/register" : "/auth";
            if (!url) url = `${host}${prefix}/graphql/auth`;

            const query = `mutation Login($input:LoginInput!) {
                login(input:$input) {accessToken,refreshToken}
            }`;
            const variables = { input: { ...form, type: "OTP" } }
            const data = await window.$ajaxGraphql(query, variables, url)

            if (data.errors) {
                error.value = data.errors[0]?.message || 'Login failed'
            } else if (data.login) {
                user.value = data.login
                accessToken.value = true
                refreshToken.value = true

                localStorage.setItem('accessToken', JSON.stringify(accessToken.value))
                localStorage.setItem('refreshToken', JSON.stringify(refreshToken.value))

                if (loadUser) {
                    await getAuthorizedUser()
                }
            }

            return data
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Login failed'
        } finally {
            isLoading.value = false
        }

        return null
    }

    const loginWithGoogle = async (token: string, url?: string) => {
        isLoading.value = true
        error.value = null

        try {
            let host = window.location.origin;
            let moduleName = import.meta.env.VITE_MODULE_NAME;
            let prefix = (moduleName == 'register') ? "/register" : "/auth";
            if (!url) url = `${host}${prefix}/graphql/auth`;

            const query = `mutation Login($input:LoginInput!) {
                login(input:$input) {accessToken,refreshToken}
            }`;
            const variables = { input: { accessToken: token, type: "GOOGLE", username: "", usernameType: "email" } }
            const data = await window.$ajaxGraphql(query, variables, url)

            if (data.errors) {
                error.value = data.errors[0]?.message || 'Google Login failed'
            } else if (data.login) {
                user.value = data.login
                accessToken.value = true
                refreshToken.value = true

                localStorage.setItem('accessToken', JSON.stringify(accessToken.value))
                localStorage.setItem('refreshToken', JSON.stringify(refreshToken.value))

                await getAuthorizedUser()
            }

            return data
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Google Login failed'
        } finally {
            isLoading.value = false
        }
        return null
    }

    const register = async (form: RegisterForm, url?: string) => {
        isLoading.value = true
        error.value = null

        try {
            let host = window.location.origin;
            let moduleName = import.meta.env.VITE_MODULE_NAME;
            let prefix = (moduleName == 'register') ? "/register" : "/auth";
            if (!url) url = `${host}${prefix}/graphql/auth`;

            const query = `mutation Register($input:RegistrationInput!) {
                register(input:$input) {status,message,data}
            }`;
            const variables = { input: form }
            const data = await window.$ajaxGraphql(query, variables, url)

            if (data.errors) {
                error.value = data.errors[0]?.message || 'Registration failed'
            } else if (data.register && data.register.status && data.register.data) {
                return data.register.data;
            } else {
                console.info("register", data);
            }
        } catch (err) {
            error.value = err instanceof Error ? err.message : 'Registration failed'
        } finally {
            isLoading.value = false
        }

        return null
    }

    const logout = async () => {
        var status = await requestLogout();

        if (status) {
            user.value = null;
            accessToken.value = null;
            refreshToken.value = null;

            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
        }
    }

    const getAuthorizedUser = async () => {
        var data = await requestCurrentUser();

        if (!data) {
            var status = await refreshAccessToken();

            if (status) {
                data = await requestCurrentUser();
            }
        }

        if (data) {
            user.value = data;

            if(Array.isArray(data.permissions)) {
                permissions.value = data.permissions;

                if (user.value) {
                    user.value.permissions = undefined;
                }
            }
            accessToken.value = true;
            refreshToken.value = true;

            setUser(data);
            localStorage.setItem('accessToken', JSON.stringify(accessToken.value));
            localStorage.setItem('refreshToken', JSON.stringify(refreshToken.value));
        } else {
            logout();
        }
    }

    const requestCurrentUser = async (moduleName?: string, url?: string) => {
        if (!moduleName) moduleName = import.meta.env.VITE_MODULE_NAME;

        let host = window.location.origin;
        let prefix = "/auth";
        if (!url) url = `${host}${prefix}/me${moduleName ? `/${moduleName}` : ``}`;

        var data = await fetch(`${url}`, {
            method: 'GET',
            credentials: 'include',  // Ensure cookies are sent with the request
            headers: {
                "Accept": "application/json",
                "Accept-Content": "application/json",
            }
        }).then(res => {
            var statusCode = res.status;

            if ([401, 402, 403].includes(statusCode)) {
                return {
                    code: statusCode,
                    status: "SUCCESS",
                }
            }

            return res.json()
        }).catch(err => console.error('Error:', err));

        if (data && data.id) {
            return data;
        } else if (data && [401, 402, 403].includes(data.code)) {
            return null;
        }

        return null;
    }

    const requestLogout = async (url?: string) => {
        var data = await fetch(`${url || '/auth/logout'}`, {
            method: 'GET',
            credentials: 'include',  // Ensure cookies are sent with the request
            headers: {
                "Accept": "application/json",
                "Accept-Content": "application/json",
            }
        }).then((res) => {
            var statusCode = res.status;
            
            if ([401, 402, 403].includes(statusCode)) {
                return {
                    code: statusCode,
                    status: "SUCCESS",
                }
            }

            return res.json()
        })
        .catch(err => console.error('Error:', err));

        if (data && data.status == "SUCCESS") {
            return true;
        }

        return false;
    }


    const requestMemberInfo = async () => {
        var res = await window.$ajaxGet("/member-info");

        console.log("requestMemberInfo", res)
        if(res) {
            member.value = res;
        }

        return res;
    }

    const refreshAccessToken = window.$refreshAccessToken

    const setUser = (data: User) => {
        console.info("Authorized user", data);
        user.value = data;
    }

    const setToken = (tokenValue: string) => {
        accessToken.value = tokenValue
        localStorage.setItem('accessToken', tokenValue)
    }

    const setRefreshToken = (tokenValue: string) => {
        refreshToken.value = tokenValue
        localStorage.setItem('refreshToken', tokenValue)
    }

    const restoreSession = async () => {
        await window.$refreshAccessToken(window.$moduleName);

        const savedToken = localStorage.getItem('accessToken')
        const savedRefreshToken = localStorage.getItem('refreshToken')

        if (savedToken && savedRefreshToken) {
            try {
                accessToken.value = JSON.parse(savedToken);
                refreshToken.value = JSON.parse(savedRefreshToken);

                // console.error('restoreSession', accessToken.value, refreshToken.value, isAuthenticated.value);
            } catch (e) {
                logout()
            }
        }
    }


    const redirectToAuth = (redirect?: string, moduleName?: string) => {
        let query = [];
        moduleName = moduleName || import.meta.env.VITE_MODULE_NAME;

        if (window.location.pathname != import.meta.env.VITE_APP_BASE_URL) {
            if (moduleName) query.push(`moduleName=${moduleName}`);
            if (redirect) query.push(`redirect=${redirect}`);

            let url = `${window.$baseUrl}${import.meta.env.DEV ? '/dev' : ''}/auth` + (query.length > 0 ? `?${query.join('&')}` : ``);

            window.location.replace(url)
        }
    }

    return {
        user,
        error,
        member,
        isLogin,
        username: fullName,
        isLoading,
        profileUrl,
        permissions,
        accessToken,
        refreshToken,
        isAuthenticated,

        sendOTP,
        login,
        loginWithGoogle,
        logout,
        register,
        getAuthorizedUser,
        setUser,
        setToken,
        setRefreshToken,
        restoreSession,
        redirectToAuth,
        requestMemberInfo,
    }
})
